import {
  IndividualRefreshEvent,
  IndividualDeleteEvent,
  CollectionSizeUpdateEvent,
} from '@/rec-cache/rec-cache-events'

import { Jsonld } from '@/rec-cache/rec-cache-types'

// TODO move into seperate module. Create object Hydra?
const hydraCollection = 'http://www.w3.org/ns/hydra/core#Collection'
const hydraMember = 'http://www.w3.org/ns/hydra/core#member'
const hydraView = 'http://www.w3.org/ns/hydra/core#view'
const hydraTotalItems = 'http://www.w3.org/ns/hydra/core#totalItems'
const hydraNext = 'http://www.w3.org/ns/hydra/core#next'

const jsonldType = Jsonld.type

const wsUrl = 'wss://events.webofbuildings.com'
const wsRetryTimeout = 5000
const eventAggregationTimeout = 3000

class RequestConfig {
  constructor(url, headers, method = 'get') {
    this.url = url
    this.headers = headers
    this.method = method
  }
}

class Request {
  constructor(config, id = null) {
    this.id = id || Math.random().toString(36).substring(2, 10)
    this.config = config || {}
  }
}

class WobClient {
  constructor({
    pageSize = 200,
    wobOrigin = 'https://webofbuildings.com',
    wobPath = '/rec',
    token = null,
    isAuthenticated = null,
  } = {}) {
    console.log('wob-client.wob-client.constructor')

    // Set the auth token.
    this.token = token
    // TODO is autheticated may potentially be a getter based on token...
    this.isAuthenticated = isAuthenticated

    // Holds user information when signed in.
    this.user = {}

    // Define and initialize variables for forming the WOB API request.
    this.wobOrigin = wobOrigin
    this.wobPath = wobPath
    this.wobQueryFlags = `?expand=true&page=0&size=${pageSize}&resolvedata=false`
    this.wobAuthHeader = token
      ? (this.wobAuthHeader = { Authorization: `Bearer ${this.token}` })
      : {}

    // Initialize request queue.
    this.queue = []
    this.abortReqId = null

    // Initialize the size of the collection.
    this.collectionSize = null

    // Initialize the web worker.
    this.worker = new Worker(new URL('./wob-client-worker.js', import.meta.url))

    // Event handler for the web worker reading in the background.
    // TODO implement logic for request exceptions.
    // TODO the worker logic shall not be syncronous, one at a time, but allow for concurrency.
    this.worker.onmessage = (message) => {
      const { data: response } = message

      console.log('wob-client.wob-client.worker-on-message', response)

      // The response is expanded JSON LD comprising array of graphs. The first element is the collection.
      const { id: requestId, data: jsonld } = response

      const [element] = jsonld
      const {
        [jsonldType]: [type],
      } = element

      // TODO refactor so that request queue logic including abort is shared between individual and collection.
      if (type != hydraCollection) {
        // Result of a single individual read.
        console.log(
          'wob-client.wob-client.worker-on-message.individual',
          element
        )

        // Remove finalized request from que based on filter
        this.queue = this.queue.filter((r) => r.id != requestId)

        // Start next pending request cycle if queue is not empty.
        const [first] = this.queue
        if (first) this.worker.postMessage(first)

        dispatchEvent(
          new CustomEvent(IndividualRefreshEvent, { detail: element })
        )

        return
      }

      // const [collection] = jsonld
      const {
        [hydraMember]: members,
        [hydraTotalItems]: [totalItems],
        [hydraView]: [view] = [],
      } = element

      // Send collection size event if changed.
      // TODO also send at zero sized collections... initially if empty that is.
      if (totalItems['@value'] != this.collectionSize) {
        console.log(
          'wob-client.wob-client.worker-on-message.size-event',
          totalItems['@value'],
          this.collectionSize
        )

        this.collectionSize = totalItems['@value']
        dispatchEvent(
          new CustomEvent(CollectionSizeUpdateEvent, {
            detail: this.collectionSize,
          })
        )
      }

      // A token change will ensure that ongoing paginated responses will not initiate new requests.
      // Nor will they return any values to the cache.
      if (requestId != this.abortReqId) {
        // Extract the next page from the partial collection view, if present.
        const next = view?.[hydraNext]?.[0]?.['@id']

        // Remove finalized request from que based on filter
        this.queue = this.queue.filter((r) => r.id != requestId)

        // For paginated results add the next page to the queue, reuse request id.
        if (next)
          this.queue.push(
            new Request(new RequestConfig(next, this.wobAuthHeader), requestId)
          )

        // Start next pending request cycle if queue is not empty.
        const [first] = this.queue
        if (first) this.worker.postMessage(first)

        // Issue refresh event for each read individual in members array.
        members.forEach((m) => {
          dispatchEvent(new CustomEvent(IndividualRefreshEvent, { detail: m }))
        })
      }
    }

    this.worker.onerror = (error) => {
      console.log('wob-client.wob-client.worker-on-error', error)
    }

    //
    // Websocket implementation.
    //

    const events = []

    const connection = (wsUrl) => {
      let ws = new WebSocket(wsUrl)
      console.log('wob-client.wob-client.connection.connecting', wsUrl)

      ws.onopen = () => {
        console.log('wob-client.wob-client.connection.connected', wsUrl)

        ws.onclose = () => {
          console.log('wob-client.wob-client.connection.disconnected')
          connection(ws.url)
        }

        ws.onmessage = (message) => {
          console.log('wob-client.wob-client.connection.message', message)
          try {
            const event = JSON.parse(message.data)
            events.push(event)
          } catch (error) {
            console.log('wob-client.wob-client.connection.parsing-error', error)
          }
        }
      }

      ws.onerror = () => {
        console.log('wob-client.wob-client.connection.error')

        setTimeout(() => {
          connection(ws.url)
        }, wsRetryTimeout)
      }
    }

    // Start web socket connection.
    connection(wsUrl)

    // Regularly aggregate all received events and execute delete and reads.
    // TODO in a later iteration the relationships may be dealt with without reading.
    setInterval(() => {
      const deletes = events.filter((e) => e.hasOperation == 'delete')
      const reads = events.filter((e) => e.hasOperation != 'delete')
      // Empties the array.
      events.splice(0)
      if (deletes.length || reads.length) {
        console.log('wob-client.wob-client.events.deletes', deletes)
        console.log('wob-client.wob-client.events.reads', reads)

        // Only read an individual once if several events have been received.
        const ids = [...new Set(reads.map((r) => r.hasSubject))]
        console.log('wob-client.wob-client.events.ids', ids)

        ids.forEach((id) => this.readOneRequest(id))

        // Issue delete events
        deletes.forEach((e) => {
          dispatchEvent(
            new CustomEvent(IndividualDeleteEvent, { detail: e.hasSubject })
          )
        })
      }
    }, eventAggregationTimeout)
  }

  init() {
    console.log('wob-client.wob-client.init')
  }

  queueRequest(request) {
    console.log('wob-client.wob-client.add-request', request)
    // TODO may push to queue first to ensure that the worker doenst return immediately and the event handler doesnt find the request in teh queue.
    if (this.queue.length == 0) this.worker.postMessage(request)
    this.queue.push(request)
  }

  readAllRequest() {
    console.log('wob-client.wob-client.read-all-request')
    return this.queueRequest(
      new Request(
        new RequestConfig(
          `${this.wobOrigin}${this.wobPath}${this.wobQueryFlags}`,
          this.wobAuthHeader
        )
      )
    )
  }

  readOneRequest(id) {
    return this.queueRequest(
      new Request(
        new RequestConfig(
          `${this.wobOrigin}${this.wobPath}/${encodeURIComponent(id)}${
            this.wobQueryFlags
          }`,
          this.wobAuthHeader
        )
      )
    )
  }

  // TODO deprecated, remove at refactor...
  // signIn(token) {
  //   this.token = token
  //   this.wobAuthHeader = { Authorization: `Bearer ${this.token}` }
  // }

  setToken(token) {
    console.log('wob-client.wob-client.set-token', token)

    if (this.token == token) return

    // Abort ongoing read cycles at token change.
    // Log ongoing request to abort ongoing paginated request cycle.
    const [currentReq] = this.queue
    this.abortReqId = currentReq?.id
    this.queue = []

    this.token = token
    this.wobAuthHeader = token ? { Authorization: `Bearer ${this.token}` } : {}
  }
}

export { WobClient }
